// controllers/how_it_works_animations_controller.js
import { Controller } from "@hotwired/stimulus"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

export default class extends Controller {
    static targets = ["featureRow", "featureListItem", "stepNumber"]

    connect() {
        // kill any leftovers (Turbo nav)
        ScrollTrigger.getAll().forEach(t => t.kill())

        // wait for fonts/images & mobile layout to settle
        this._initAfterImages().then(() => {
            this._buildAnimations()
            ScrollTrigger.refresh() // important on iOS after address bar/layout changes
        })

        // extra safety: refresh on orientation change
        this._onResize = () => ScrollTrigger.refresh()
        window.addEventListener("orientationchange", this._onResize)
        window.addEventListener("resize", this._onResize, { passive: true })
    }

    disconnect() {
        window.removeEventListener("orientationchange", this._onResize)
        window.removeEventListener("resize", this._onResize)
        ScrollTrigger.getAll().forEach(t => t.kill())
    }

    async _initAfterImages() {
        const imgs = Array.from(this.element.querySelectorAll("img"))
        await Promise.all(
            imgs.map(img =>
                img.complete ? Promise.resolve() :
                    new Promise(res => img.addEventListener("load", res, { once: true }))
            )
        )
        // small async delay so Safari finalizes layout
        await new Promise(r => setTimeout(r, 0))
    }

    _buildAnimations() {
        // mobile + desktop variants using matchMedia
        ScrollTrigger.matchMedia({

            // Mobile: keep transforms modest and use a forgiving start
            "(max-width: 767.98px)": () => {
                this.featureRowTargets.forEach((row, i) => {
                    const listItems   = row.querySelectorAll('[data-how-it-works-animations-target="featureListItem"]')
                    const numberCircle = row.querySelector('[data-how-it-works-animations-target="stepNumber"]')

                    // 1) DO NOT pre-hide on mobile (remove the gsap.set with opacity 0)
                    // gsap.set(listItems, { y: 24, opacity: 0 })   <-- delete this
                    gsap.set(numberCircle, { scale: 1 })

                    // Step 1 sits at the top; fire earlier so it always runs
                    const startPos = i === 0 ? "top 99%" : "top 92%"

                    const tl = gsap.timeline({
                        defaults: { ease: "power3.out" },
                        scrollTrigger: {
                            trigger: row,
                            start: startPos,
                            end: "bottom 60%",
                            once: true,
                            invalidateOnRefresh: true,
                            // markers: true,
                        },
                    })

                    // 2) Animate FROM so content is visible by default; no pre-hide
                    tl.from(listItems, {
                        y: 16,
                        autoAlpha: 0,
                        duration: 0.6,
                        stagger: 0.08,
                        immediateRender: false
                    })
                        .to(numberCircle, { scale: 1.15, duration: 0.18 }, "+=0.05")
                        .to(numberCircle, { scale: 1, duration: 0.18 })

                    // 3) Real setTimeout fallback (not on the timeline object)
                    setTimeout(() => {
                        listItems.forEach(el => {
                            el.style.opacity = "1"
                            el.style.visibility = "visible"
                            el.style.transform = "none"
                        })
                    }, 1500)
                })
            },

            // Tablet/desktop: your original behavior
            "(min-width: 768px)": () => {
                this.featureRowTargets.forEach((row) => {
                    const listItems = row.querySelectorAll('[data-how-it-works-animations-target="featureListItem"]')
                    const numberCircle = row.querySelector('[data-how-it-works-animations-target="stepNumber"]')

                    gsap.set(listItems, { y: 60, opacity: 0 })
                    gsap.set(numberCircle, { scale: 1 })

                    const tl = gsap.timeline({
                        defaults: { ease: "power3.out" },
                        scrollTrigger: {
                            trigger: row,
                            start: "top 90%",
                            invalidateOnRefresh: true,
                            // markers: true,
                        },
                    })

                    tl.to(listItems, {
                        y: 0,
                        opacity: 1,
                        duration: 0.9,
                        stagger: 0.1
                    }).to(numberCircle, {
                        scale: 1.25,
                        duration: 0.2
                    }, "+=0.1").to(numberCircle, {
                        scale: 1,
                        duration: 0.2
                    })
                })
            }
        })
    }
}